import { useState, useMemo } from 'react';
import { Stack, Paper, Title, Text, Select, Grid, Skeleton, Alert, Group, Badge } from '@mantine/core';
import { useOutletRuntime } from '@/hooks/usePowerData';
import { OutletRuntimeChart } from './OutletRuntimeChart';
import type { OutletRuntime } from '@/api/power';

interface OutletRuntimeSectionProps {
  dateRange: [Date, Date];
}

export function OutletRuntimeSection({ dateRange }: OutletRuntimeSectionProps) {
  const [selectedOutletKey, setSelectedOutletKey] = useState<string | null>(null);

  const { data, isLoading, error } = useOutletRuntime(dateRange[0], dateRange[1]);

  // source_key is the unique key — "did::display_name_snapshot"
  const outletOptions = useMemo(() => {
    if (!data?.outlets) return [];
    return data.outlets.map(o => ({
      value: o.source_key,
      label: o.outlet_name,
    }));
  }, [data]);

  const selectedOutlet: OutletRuntime | undefined = useMemo(() => {
    if (!selectedOutletKey || !data?.outlets) return undefined;
    return data.outlets.find(o => o.source_key === selectedOutletKey);
  }, [data, selectedOutletKey]);

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Title order={3} mb="md">Outlet Runtime</Title>
        <Skeleton height={36} width={250} radius="sm" />
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading outlet runtime">
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Title order={3} mb="md">Outlet Runtime</Title>

        <Select
          label="Select outlet"
          placeholder="Choose an outlet"
          data={outletOptions}
          value={selectedOutletKey}
          onChange={setSelectedOutletKey}
          searchable
          clearable
          maw={300}
        />

        {!selectedOutletKey && (
          <Text c="dimmed" mt="md" ta="center">
            Select an outlet to view ON/OFF runtime analysis
          </Text>
        )}
      </Paper>

      {selectedOutlet && (
        <>
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <Title order={4}>Total Duration</Title>
                  {Object.entries(selectedOutlet.total_by_state)
                    .sort()
                    .map(([state, hours]) => {
                      const totalHours = Object.values(selectedOutlet.total_by_state)
                        .reduce((sum, h) => sum + h, 0);
                      const pct = totalHours > 0 ? (hours / totalHours * 100) : 0;

                      return (
                        <div key={state}>
                          <Group gap="xs">
                            <Badge color={state === 'ON' ? 'green' : 'gray'} size="sm">
                              {state}
                            </Badge>
                          </Group>
                          <Text size="xl" fw={600}>{hours.toFixed(1)} hrs</Text>
                          <Text size="sm" c="dimmed">{pct.toFixed(1)}%</Text>
                        </div>
                      );
                    })}
                </Stack>
              </Paper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 8 }}>
              <OutletRuntimeChart
                periodSummary={selectedOutlet.period_summary}
                bucketSize={data?.bucket_size ?? 'day'}
                outletName={selectedOutlet.outlet_name}
              />
            </Grid.Col>
          </Grid>

          {data?.data_through && (
            <Text size="sm" c="dimmed" ta="center">
              Data shown through {new Date(data.data_through).toLocaleString()}. Totals will update on next sync.
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}
